/* =============================================================================
 * PDP FAQs (fscc739j) — slot-derived
 *
 * Rewrite for the snippet content slots model (foundation §3-§5). FAQ items
 * arrive inline via `entry.content.items` from a data-ingestion-owned simple
 * slot (`product.metafields.spectrum.faqs_<state>`). The snippet renders
 * server-side; this runtime adds:
 *
 *   1. <sai-fscc739j> custom element with applyVariant(presentation, content)
 *      so cross-experience and content-experiment swaps rebuild the accordion.
 *   2. __spectrumAi.snippet.bind(node, callback) for variant resolution AND
 *      to capture the analytics `track` handle.
 *   3. Three analytics events through track (faqs:open, faqs:close, faqs:ask).
 *      Catalog: packages/snippet-library/docs/faqs.analytics.md.
 *   4. Existing Ask AI block (POST {proxyBase}/faq/ask) — behaviour unchanged
 *      apart from a single `track('faqs:ask', { question_text })` call before
 *      the network request.
 *
 * No-bind fallback: if window.__spectrumAi is absent (e.g. local dev without
 * the bootstrap SDK loaded, or a theme that hasn't installed the embed), the
 * SSR'd accordion remains usable with native <details> behaviour, the Ask AI
 * block hides itself, and analytics are silently skipped (track = noop).
 *
 * Trust boundary:
 *   Backend-supplied content is trusted by contract. buildItem emits
 *   item.answerHtml raw — no sanitisation, no transform. If the data shape
 *   is wrong, the playbook author / portal write path is responsible, not
 *   this runtime.
 *
 * data-faq-id derivation:
 *   The backend FaqSlotItem shape does not carry an `id`. The SSR template
 *   derives data-faq-id from `item.question | handleize`; this runtime
 *   mirrors that with handleize() so applyVariant rebuilds produce the
 *   same id. Stable across reorders and draft→live promotes; changes when
 *   the question text changes (intentional — different question = different
 *   FAQ for analytics purposes).
 * ============================================================================= */

;(() => {
  if (window.__sai_fscc739j_initialized__) return
  window.__sai_fscc739j_initialized__ = true

  const SNIPPET_ID = 'fscc739j'
  const TAG = 'sai-fscc739j'
  const FEATURE_SLUG = 'faqs'

  // Mirrors Shopify Liquid's `| handleize` — used to derive data-faq-id from
  // the question text. The SSR template uses `{{ item.question | handleize }}`;
  // applyVariant rebuilds must produce the same id so analytics dedup keys
  // match across page-load and post-rebuild.
  function handleize(s) {
    if (s == null) return ''
    return String(s)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  function noopTrack() {}

  // safeTrack wraps every track call so a malformed payload or downstream
  // analytics bug never breaks the accordion or the Ask AI form.
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (_) {
        /* swallow — analytics is best-effort */
      }
    }
  }

  function buildItem(item, index) {
    if (!item) return ''
    const id = handleize(item.question)
    const category = item.category || ''
    const question = item.question || ''
    const answerHtml = item.answerHtml || ''
    return `<details class="sai-fscc739j__item" data-faq-id="${id}" data-faq-category="${category}" data-index="${index}" role="listitem"><summary class="sai-fscc739j__summary"><span class="sai-fscc739j__question">${question}</span><span class="sai-fscc739j__chevron" aria-hidden="true"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" focusable="false"><path d="M3 5L7 9L11 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span></summary><div class="sai-fscc739j__answer">${answerHtml}</div></details>`
  }

  if (!customElements.get(TAG)) {
    class SaiFaqs extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return
        this._initialized = true

        // Defaults; replaced via setAnalytics() once bindAllContainers runs.
        this._track = noopTrack

        // Per-FAQ open timestamps so :close can report dwell_ms.
        this._openedAt = new Map()

        this._initAccordion()
        this._initAsk()
      }

      // Called once per page-load by bindAllContainers, before the bind
      // callback fires. Until then, all tracking is a noop.
      setAnalytics(track) {
        this._track = typeof track === 'function' ? safeTrack(track) : noopTrack
      }

      // Event-delegated toggle listener on the accordion container. The native
      // `toggle` event does NOT bubble, so we must listen in the capture
      // phase. Delegation lets us attach the listener once at init time;
      // applyVariant's innerHTML rebuild does not need to rebind because the
      // accordion container itself is preserved.
      _initAccordion() {
        const accordion = this.querySelector('.sai-fscc739j__accordion')
        if (!accordion || this._toggleBound) return
        this._toggleBound = true
        accordion.addEventListener(
          'toggle',
          (e) => {
            const target = e.target
            if (!target || typeof target.closest !== 'function') return
            const details = target.closest('.sai-fscc739j__item')
            if (details && this.contains(details)) {
              this._onItemToggle(details)
            }
          },
          true,
        )
      }

      _onItemToggle(details) {
        // Single-open: when one answer opens, close the others. Setting
        // `.open = false` dispatches a `toggle` on each closed item, so their
        // :close analytics still flow through this same delegated handler.
        if (details.open && this.dataset.singleOpen === 'true') {
          const acc = this.querySelector('.sai-fscc739j__accordion')
          if (acc) {
            for (const other of acc.querySelectorAll('.sai-fscc739j__item[open]')) {
              if (other !== details) other.open = false
            }
          }
        }

        const faqId = details.getAttribute('data-faq-id') || ''
        const questionEl = details.querySelector('.sai-fscc739j__question')
        const faqQuestion = questionEl ? (questionEl.textContent || '').trim() : ''
        const faqCategory = details.getAttribute('data-faq-category') || ''
        const positionAttr = details.getAttribute('data-index')
        const position = positionAttr == null ? 0 : Number.parseInt(positionAttr, 10) || 0

        const basePayload = {
          faq_id: faqId,
          faq_question: faqQuestion,
          position,
        }
        if (faqCategory) basePayload.faq_category = faqCategory

        if (details.open) {
          this._openedAt.set(faqId, Date.now())
          this._track(`${FEATURE_SLUG}:open`, basePayload)
        } else {
          const openedAt = this._openedAt.get(faqId)
          this._openedAt.delete(faqId)
          // dwell_ms is null when :close fires without a paired :open in this
          // page-view (defensive — should not happen in practice; would
          // indicate a missed setAnalytics race or a stale toggle replay).
          const dwell_ms = openedAt != null ? Date.now() - openedAt : null
          this._track(`${FEATURE_SLUG}:close`, { ...basePayload, dwell_ms })
        }
      }

      _initAsk() {
        this._ask = null
        const spectrumAi = window.__spectrumAi || null
        const askEl = this.querySelector('[data-spectrum-ai-required]')

        if (!spectrumAi) {
          // No Spectrum AI — accordion still works; hide Ask block.
          if (askEl) askEl.hidden = true
          return
        }

        const form = this.querySelector('[data-action="ask"]')
        const input = form ? form.querySelector('.sai-fscc739j__input') : null
        const sendBtn = form ? form.querySelector('.sai-fscc739j__send') : null
        const exchangeSlot = this.querySelector('[data-slot="exchange"]')

        if (!form || !input || !exchangeSlot) return

        // Resolve the brand-specific app-proxy base. The Spectrum AI app embed
        // publishes window.__spectrumAi.baseProxyUrl (e.g. /apps/spectrum_ai_<org>),
        // which is authoritative. Fall back to the data attribute, then the literal.
        const proxyBase =
          spectrumAi?.baseProxyUrl || this.getAttribute('data-proxy-base') || '/apps/spectrum'
        const productId = Number(this.getAttribute('data-product-id'))
        const productHandle = this.getAttribute('data-product-handle') || ''

        const ask = {
          form,
          input,
          sendBtn,
          exchangeSlot,
          proxyBase,
          productId,
          productHandle,
          rateLimitedUntil: 0,
          inflight: false,
        }
        this._ask = ask

        form.addEventListener('submit', (e) => {
          e.preventDefault()
          if (ask.inflight) return
          this._ask_submit(input.value)
        })
      }

      _ask_buildTargetingContext() {
        const spectrumAi = window.__spectrumAi || null
        const builder =
          spectrumAi?.targeting && typeof spectrumAi.targeting.buildContext === 'function'
            ? spectrumAi.targeting.buildContext
            : null
        if (builder) {
          try {
            return builder()
          } catch (_) {
            /* fall through */
          }
        }
        let device = 'desktop'
        const w = window.innerWidth || 0
        if (w > 0 && w < 768) device = 'mobile'
        else if (w >= 768 && w < 1024) device = 'tablet'
        const customer = spectrumAi?.customer || null
        return {
          page: {
            type: 'product',
            url: window.location.href,
            path: window.location.pathname,
          },
          device: { type: device },
          currentUser: customer
            ? { isAnonymous: false, id: String(customer.id), email: customer.email }
            : { isAnonymous: true },
        }
      }

      _ask_setBusy(busy) {
        const ask = this._ask
        if (!ask) return
        ask.inflight = busy
        if (ask.sendBtn) ask.sendBtn.disabled = busy
        ask.input.disabled = busy
      }

      _ask_renderExchange(state) {
        const ask = this._ask
        if (!ask) return
        const slot = ask.exchangeSlot
        slot.replaceChildren()

        const q = document.createElement('div')
        q.className = 'sai-fscc739j__exchange-question'
        q.textContent = state.question
        slot.appendChild(q)

        if (state.status === 'loading') {
          const shimmer = document.createElement('div')
          shimmer.className = 'sai-fscc739j__shimmer'
          const line1 = document.createElement('div')
          line1.className = 'sai-fscc739j__shimmer-line'
          const line2 = document.createElement('div')
          line2.className = 'sai-fscc739j__shimmer-line'
          shimmer.appendChild(line1)
          shimmer.appendChild(line2)
          slot.appendChild(shimmer)
        } else if (state.status === 'success') {
          const answer = document.createElement('div')
          answer.className = 'sai-fscc739j__exchange-answer'
          answer.innerHTML = state.answerHtml || ''
          slot.appendChild(answer)

          const chips = (state.followUps || [])
            .filter((t) => typeof t === 'string' && t)
            .slice(0, 3)
          if (chips.length) {
            const wrap = document.createElement('div')
            wrap.className = 'sai-fscc739j__followups'
            for (const text of chips) {
              const btn = document.createElement('button')
              btn.type = 'button'
              btn.className = 'sai-fscc739j__followup'
              btn.textContent = text
              btn.addEventListener('click', () => {
                if (this._ask?.inflight) return
                this._ask_submit(text)
              })
              wrap.appendChild(btn)
            }
            slot.appendChild(wrap)
          }
        } else if (state.status === 'error') {
          const err = document.createElement('div')
          err.className = 'sai-fscc739j__exchange-error'

          const msg = document.createElement('span')
          msg.textContent = state.message
          err.appendChild(msg)

          if (state.retryable) {
            const retry = document.createElement('button')
            retry.type = 'button'
            retry.className = 'sai-fscc739j__retry'
            retry.textContent = 'Retry'
            retry.addEventListener('click', () => {
              if (this._ask?.inflight) return
              this._ask_submit(state.question)
            })
            err.appendChild(retry)
          }

          slot.appendChild(err)
        }

        slot.hidden = false
      }

      _ask_submit(question) {
        const ask = this._ask
        if (!ask) return
        const trimmed = (question || '').trim()
        if (!trimmed) return

        const now = Date.now()
        if (now < ask.rateLimitedUntil) {
          const waitS = Math.ceil((ask.rateLimitedUntil - now) / 1000)
          this._ask_renderExchange({
            question: trimmed,
            status: 'error',
            message: `Please wait ${waitS}s before asking another question.`,
            retryable: false,
          })
          return
        }

        // Intent signal — fires BEFORE the fetch (mirrors svmk8tqx
        // add_to_cart pattern). Pair with :ask_response when product asks.
        this._track(`${FEATURE_SLUG}:ask`, { question_text: trimmed })

        // Loading state up first; clear input so the user can start typing again.
        this._ask_renderExchange({ question: trimmed, status: 'loading' })
        ask.input.value = ''
        this._ask_setBusy(true)

        const body = {
          question: trimmed,
          product: { id: ask.productId, handle: ask.productHandle },
          targetingContext: this._ask_buildTargetingContext(),
        }

        fetch(`${ask.proxyBase}/faq/ask`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
          .then((res) => {
            if (res.status === 429) {
              const retryAfter = Number.parseInt(res.headers.get('Retry-After') || '0', 10)
              if (retryAfter > 0) ask.rateLimitedUntil = Date.now() + retryAfter * 1000
            }
            return res.json().then((json) => ({ res, json }))
          })
          .then((out) => {
            const json = out.json || {}
            if (out.res.ok && json.success === true && json.data) {
              this._ask_renderExchange({
                question: trimmed,
                status: 'success',
                answerHtml: json.data.answer?.html,
                followUps: json.data.followUps || [],
              })
            } else {
              const msg =
                (json && (json.error || json.message)) ||
                "We couldn't get an answer right now. Please try again."
              const status = out.res.status
              const retryable = status === 429 || status >= 500
              this._ask_renderExchange({
                question: trimmed,
                status: 'error',
                message: msg,
                retryable,
              })
            }
          })
          .catch(() => {
            this._ask_renderExchange({
              question: trimmed,
              status: 'error',
              message: "We couldn't reach the server. Please check your connection and try again.",
              retryable: true,
            })
          })
          .then(() => {
            this._ask_setBusy(false)
          })
      }

      // Variant resolution entry point. Re-renders the inner DOM from the
      // resolved presentation + content. Called by the bind callback on every
      // $spectrum:variant_resolved event.
      applyVariant(presentation, content) {
        const heading =
          presentation && typeof presentation.heading === 'string' ? presentation.heading : null
        const subheading =
          presentation && typeof presentation.subheading === 'string'
            ? presentation.subheading
            : null
        const qaHeading =
          presentation && typeof presentation.qa_heading === 'string'
            ? presentation.qa_heading
            : null
        const placeholder =
          presentation && typeof presentation.input_placeholder === 'string'
            ? presentation.input_placeholder
            : null
        const allItems = content && Array.isArray(content.items) ? content.items : []
        // Cap to data-max-faqs (0/absent = show all) so JS rebuilds match SSR.
        const maxFaqs = Number.parseInt(this.dataset.maxFaqs || '0', 10) || 0
        const items = maxFaqs > 0 ? allItems.slice(0, maxFaqs) : allItems

        if (heading != null) {
          const h = this.querySelector('.sai-fscc739j__heading')
          if (h) h.textContent = heading
        }
        if (subheading != null) {
          let sub = this.querySelector('.sai-fscc739j__subheading')
          if (!sub) {
            sub = document.createElement('p')
            sub.className = 'sai-fscc739j__subheading'
            const header = this.querySelector('.sai-fscc739j__header')
            if (header) header.appendChild(sub)
          }
          sub.textContent = subheading
          sub.hidden = !subheading
        }
        if (qaHeading != null) {
          const h = this.querySelector('.sai-fscc739j__qa-heading')
          if (h) h.textContent = qaHeading
        }
        if (placeholder != null) {
          const input = this.querySelector('.sai-fscc739j__input')
          if (input) input.placeholder = placeholder
        }
        if (presentation && typeof presentation.show_faqs === 'boolean') {
          const faqsSection = this.querySelector('[data-faqs-section]')
          if (faqsSection) faqsSection.hidden = !presentation.show_faqs
        }
        if (presentation && typeof presentation.show_qa === 'boolean') {
          const ask = this.querySelector('.sai-fscc739j__ask')
          if (ask) ask.hidden = !presentation.show_qa
        }
        if (presentation && typeof presentation.show_branding === 'boolean') {
          let footer = this.querySelector('[data-branding]')
          if (!footer) {
            footer = document.createElement('footer')
            footer.className = 'sai-fscc739j__footer'
            footer.setAttribute('data-branding', '')
            footer.innerHTML =
              '<span class="sai-fscc739j__brand">Powered by Spectrum AI</span>' +
              '<span class="sai-fscc739j__divider" aria-hidden="true">|</span>' +
              '<span class="sai-fscc739j__disclaimer">This answer is AI-generated. Please double check important information.</span>'
            const container = this.querySelector('.sai-fscc739j__container')
            if (container) container.appendChild(footer)
          }
          footer.hidden = !presentation.show_branding
        }

        let accordion = this.querySelector('.sai-fscc739j__accordion')
        if (!accordion && items.length > 0) {
          accordion = document.createElement('div')
          accordion.className = 'sai-fscc739j__accordion'
          accordion.setAttribute('role', 'list')
          const faqsSection = this.querySelector('[data-faqs-section]')
          if (faqsSection) faqsSection.appendChild(accordion)
          this._toggleBound = false
          this._initAccordion()
        }
        if (!accordion) return
        accordion.innerHTML = items.map((item, i) => buildItem(item, i)).join('')

        // Reset dwell tracker — replaced DOM means previous open timestamps
        // would be misleading on a future :close.
        this._openedAt = new Map()

        // No rebind needed — the toggle listener is delegated in capture
        // phase on the accordion container, which is preserved across
        // applyVariant rebuilds.
      }
    }
    customElements.define(TAG, SaiFaqs)
  }

  // ── Test surface — pure helpers exposed for unit tests when the harness
  // flag is set. Production never sets the flag.
  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiFscc739j = {
      buildItem,
      safeTrack,
    }
  }

  function bindAllContainers() {
    const api = window.__spectrumAi?.snippet
    const containers = document.querySelectorAll(
      `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
    )

    if (!api?.bind) {
      // No-bind fallback: SSR'd accordion + Ask AI behave as already rendered.
      // The custom element initialises with noopTrack via connectedCallback
      // so toggles silently skip analytics. Nothing more to do here.
      return
    }

    for (const node of containers) {
      const handles = api.bind(node, ({ variant, entry }) => {
        if (!variant?.content || !entry?.content) return
        const root = node.querySelector(TAG)
        if (root && typeof root.applyVariant === 'function') {
          root.applyVariant(variant.content, entry.content)
        }
      })
      const root = node.querySelector(TAG)
      if (root && handles?.track && typeof root.setAnalytics === 'function') {
        root.setAnalytics(handles.track)
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllContainers, { once: true })
  } else {
    bindAllContainers()
  }
})()
